import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  GameState,
  IPiece,
  Position,
  EffectType,
  PieceType,
  MoveType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 13: Self Capture Block
 *
 * Prevents the AI from capturing its own pieces unless it's for critical game-winning situations.
 * Only priorities 1-7 (immediate wins) should be allowed to make self-captures.
 *
 * Uses move simulation to detect material loss of our own pieces.
 */
export class SelfCaptureBlockPriority extends BaseDecisionPriority {
  readonly priority = 16;
  readonly name = "Self Capture Block";
  readonly description =
    "Prevent self-captures unless for immediate wins (priorities 1-7)";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we must always check for self-captures
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    // Only check Rhino and Monkey moves for efficiency (only pieces that can self-capture)
    const rhinoMonkeyMoves = context.allPossibleMoves.filter(
      (moveInfo: MoveInfo) => {
        const piece = getPieceFromPosition(
          context.gameState,
          moveInfo.fromPosition
        );
        return (
          piece &&
          (piece.type === PieceType.Rhino || piece.type === PieceType.Monkey)
        );
      }
    );

    if (rhinoMonkeyMoves.length === 0) {
      this.addLog("✅ No Rhino/Monkey moves found - no self-capture risk");
      return {
        shouldEarlyExit: false,
        filteredMoves: context.allPossibleMoves,
        reasoning: "No Rhino/Monkey moves found - no self-capture risk",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Check each Rhino/Monkey move for self-capture
    const selfCaptureMoves: string[] = [];
    const blockReasons: string[] = [];
    const selfCaptureResults = new Map<
      string,
      { isCapture: boolean; reason: string }
    >();

    // First pass: Analyze all Rhino/Monkey moves and cache results
    for (const moveInfo of rhinoMonkeyMoves) {
      const moveKey = `${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}->${moveInfo.move.position.row},${moveInfo.move.position.col}`;
      const result = this.isSelfCaptureMoveBySimulation(
        moveInfo,
        context.gameState
      );
      selfCaptureResults.set(moveKey, result);

      if (result.isCapture) {
        const piece = getPieceFromPosition(
          context.gameState,
          moveInfo.fromPosition
        );
        const moveDesc = `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;
        selfCaptureMoves.push(moveDesc);
        blockReasons.push(`❌ BLOCKED: ${moveDesc} - ${result.reason}`);
      }
    }

    // Filter out self-capture moves using cached results
    const filteredMoves = context.allPossibleMoves.filter(
      (moveInfo: MoveInfo) => {
        // PERFORMANCE FIX: Only run simulation for Rhino/Monkey moves
        const piece = getPieceFromPosition(
          context.gameState,
          moveInfo.fromPosition
        );

        // If not Rhino/Monkey, always include the move (no self-capture risk)
        if (
          !piece ||
          (piece.type !== PieceType.Rhino && piece.type !== PieceType.Monkey)
        ) {
          return true;
        }

        // For Rhino/Monkey moves, check cached result
        const moveKey = `${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}->${moveInfo.move.position.row},${moveInfo.move.position.col}`;
        const cachedResult = selfCaptureResults.get(moveKey);
        if (cachedResult) {
          return !cachedResult.isCapture;
        }

        // Fallback: Run simulation if not cached (shouldn't happen)
        const result = this.isSelfCaptureMoveBySimulation(
          moveInfo,
          context.gameState
        );
        return !result.isCapture;
      }
    );

    // Summary logging
    if (selfCaptureMoves.length > 0) {
      this.addLog(
        `🛡️ SELF CAPTURE BLOCKED: Found ${selfCaptureMoves.length} self-capture moves`
      );
      this.addLog(
        `🔧 Filtered ${rhinoMonkeyMoves.length} Rhino/Monkey moves, blocked ${selfCaptureMoves.length} self-captures`
      );
    } else {
      this.addLog(
        `✅ No self-capture moves found in ${rhinoMonkeyMoves.length} Rhino/Monkey moves`
      );
    }

    return {
      shouldEarlyExit: false,
      filteredMoves: filteredMoves,
      reasoning:
        selfCaptureMoves.length > 0
          ? `Blocked ${selfCaptureMoves.length} self-capture moves`
          : "No self-capture moves found",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * CORRECT APPROACH: Simulate the move and check for material loss of our own pieces
   */
  private isSelfCaptureMoveBySimulation(
    moveInfo: MoveInfo,
    gameState: GameState
  ): { isCapture: boolean; reason: string } {
    try {
      // Get our color
      const ourColor = gameState.isLightTurn;

      // Count our pieces before the move
      const piecesBefore = this.countOurPieces(gameState, ourColor);

      // Get moving piece info
      const movingPiece = getPieceFromPosition(
        gameState,
        moveInfo.fromPosition
      );
      if (!movingPiece) {
        return { isCapture: false, reason: "No piece to move" };
      }

      // DEBUG: Special logging for Rhino moves
      const moveDesc = `${movingPiece.type} [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;
      if (movingPiece.type === PieceType.Rhino) {
        this.addLog(`🔍 DEBUGGING RHINO: ${moveDesc}`);
        this.addLog(
          `📋 Original side effects: ${moveInfo.move.sideEffects?.length || 0}`
        );
      }

      // Simulate the move with deep state cloning
      const testState = cloneFullGameState(gameState);
      const testMovingPiece = getPieceFromPosition(
        testState,
        moveInfo.fromPosition
      );

      if (!testMovingPiece) {
        return { isCapture: false, reason: "No piece to move in test state" };
      }

      // DEBUG: Validate state integrity before simulation
      if (movingPiece.type === PieceType.Rhino) {
        const originalPieceCount = this.countOurPieces(gameState, ourColor);
        const testPieceCount = this.countOurPieces(testState, ourColor);
        if (originalPieceCount !== testPieceCount) {
          this.addLog(
            `⚠️ STATE CLONE MISMATCH: Original ${originalPieceCount} vs Test ${testPieceCount}`
          );
        }
      }

      // CRITICAL FIX: For trample moves with missing side effects, manually calculate them
      let effectiveMove = moveInfo.move;

      if (
        moveInfo.move.moveType === MoveType.trample &&
        (!moveInfo.move.sideEffects || moveInfo.move.sideEffects.length === 0)
      ) {
        if (movingPiece.type === PieceType.Rhino) {
          this.addLog(`🔧 RHINO: Calculating missing trample side effects`);
        }
        effectiveMove = this.calculateTrampleSideEffects(moveInfo, testState);
        if (movingPiece.type === PieceType.Rhino) {
          this.addLog(
            `🔧 RHINO: Calculated ${
              effectiveMove.sideEffects?.length || 0
            } side effects`
          );
        }
      }

      // Apply the move with all its effects
      // Manual trample simulation since applyMoveWithSnakeRespawn might not handle side effects
      if (effectiveMove.moveType === MoveType.trample) {
        if (movingPiece.type === PieceType.Rhino) {
          this.addLog(`⚙️ RHINO: Using manual trample simulation`);
        }
        this.simulateTrampleMove(
          testState,
          moveInfo.fromPosition,
          effectiveMove.position,
          testMovingPiece,
          effectiveMove
        );
      } else {
        if (movingPiece.type === PieceType.Rhino) {
          this.addLog(`⚙️ RHINO: Using standard applyMoveWithSnakeRespawn`);
        }
        applyMoveWithSnakeRespawn(
          testState,
          moveInfo.fromPosition,
          effectiveMove,
          testMovingPiece
        );
      }

      // Validate any side effects (meadow, etc.)
      validateMeadowEffects(testState);

      // Count our pieces after the move
      const piecesAfter = this.countOurPieces(testState, ourColor);

      // Check for material loss
      const materialLoss = piecesBefore - piecesAfter;

      if (movingPiece.type === PieceType.Rhino) {
        this.addLog(
          `📊 RHINO RESULT: ${piecesBefore} -> ${piecesAfter} pieces (loss: ${materialLoss})`
        );
      }

      if (materialLoss > 0) {
        const movingPieceDesc = `${ourColor ? "Light" : "Dark"} ${
          movingPiece.type
        }${movingPiece.isKing ? " (King)" : ""}`;
        return {
          isCapture: true,
          reason: `${movingPieceDesc} loses ${materialLoss} of our own pieces`,
        };
      }

      return { isCapture: false, reason: "No material loss of our pieces" };
    } catch (error) {
      this.addLog(`❌ Simulation error: ${error}`);
      return {
        isCapture: true, // Assume self-capture on error for safety
        reason: `Simulation error: ${error}`,
      };
    }
  }

  /**
   * Manually calculate trample side effects when they're missing from move generation
   */
  private calculateTrampleSideEffects(
    moveInfo: MoveInfo,
    gameState: GameState
  ): any {
    const fromPos = moveInfo.fromPosition;
    const toPos = moveInfo.move.position;

    const rowDiff = toPos.row - fromPos.row;
    const colDiff = toPos.col - fromPos.col;
    const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
    const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

    let currentRow = fromPos.row + rowStep;
    let currentCol = fromPos.col + colStep;

    const sideEffects = [];

    this.addLog(
      `🔧 CALCULATING TRAMPLE PATH from [${fromPos.row},${fromPos.col}] to [${toPos.row},${toPos.col}]`
    );

    // Calculate trample path
    while (currentRow !== toPos.row || currentCol !== toPos.col) {
      const currentPosition = { row: currentRow, col: currentCol };
      const square = gameState.board[currentRow][currentCol];
      const piecesAtPosition = square.Pieces.slice();

      this.addLog(
        `🔧 Checking square [${currentRow},${currentCol}]: ${piecesAtPosition.length} pieces`
      );

      if (piecesAtPosition.length > 0) {
        for (const piece of piecesAtPosition) {
          this.addLog(
            `🔧 Found piece: ${piece.isLight ? "Light" : "Dark"} ${
              piece.type
            } at [${currentRow},${currentCol}]`
          );
        }

        sideEffects.push({
          id: crypto.randomUUID(),
          moveId: moveInfo.move.id,
          position: currentPosition,
          piecesToEffect: piecesAtPosition,
          effect: { type: EffectType.capture },
        });

        this.addLog(
          `🔧 Added capture side effect at [${currentRow},${currentCol}] for ${piecesAtPosition.length} pieces`
        );
      }

      currentRow += rowStep;
      currentCol += colStep;
    }

    this.addLog(
      `🔧 TRAMPLE CALCULATION COMPLETE: ${sideEffects.length} side effects`
    );

    return {
      ...moveInfo.move,
      sideEffects: sideEffects,
    };
  }

  /**
   * Manual trample simulation that applies side effects
   */
  private simulateTrampleMove(
    testState: GameState,
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    move: any
  ): void {
    this.addLog(
      `🗡️ SIMULATING TRAMPLE from [${fromPosition.row},${fromPosition.col}] to [${toPosition.row},${toPosition.col}]`
    );

    // Apply side effects first (captures along the path)
    if (move.sideEffects) {
      this.addLog(`🗡️ Processing ${move.sideEffects.length} side effects`);

      for (const sideEffect of move.sideEffects) {
        if (sideEffect.effect.type === EffectType.capture) {
          this.addLog(
            `🗡️ Capture side effect at [${sideEffect.position.row},${sideEffect.position.col}] affecting ${sideEffect.piecesToEffect.length} pieces`
          );

          for (const capturedPiece of sideEffect.piecesToEffect) {
            this.addLog(
              `🗡️ TRAMPLING: Removing ${
                capturedPiece.isLight ? "Light" : "Dark"
              } ${capturedPiece.type} at [${sideEffect.position.row},${
                sideEffect.position.col
              }]`
            );

            // Remove the piece from the test state
            const square =
              testState.board[sideEffect.position.row][sideEffect.position.col];

            // DEBUG: Show all pieces at this position before removal
            this.addLog(
              `🔍 BEFORE REMOVAL: Square [${sideEffect.position.row},${sideEffect.position.col}] has ${square.Pieces.length} pieces:`
            );
            for (let i = 0; i < square.Pieces.length; i++) {
              const p = square.Pieces[i];
              this.addLog(
                `   ${i}: ${p.isLight ? "Light" : "Dark"} ${p.type} (ID: ${
                  p.id
                })`
              );
            }
            this.addLog(
              `🔍 LOOKING FOR: ${capturedPiece.isLight ? "Light" : "Dark"} ${
                capturedPiece.type
              } (ID: ${capturedPiece.id})`
            );

            const pieceIndex = square.Pieces.findIndex(
              (p) => p.id === capturedPiece.id
            );
            if (pieceIndex !== -1) {
              square.Pieces.splice(pieceIndex, 1);
              this.addLog(
                `✅ Successfully removed piece at index ${pieceIndex}`
              );
            } else {
              // Try alternative matching strategies
              const altIndex1 = square.Pieces.findIndex(
                (p) =>
                  p.type === capturedPiece.type &&
                  p.isLight === capturedPiece.isLight
              );
              const altIndex2 = square.Pieces.findIndex(
                (p) => p.type === capturedPiece.type
              );

              this.addLog(
                `❌ Could not find piece by ID! Alt strategies: type+color=${altIndex1}, type=${altIndex2}`
              );

              // Use alternative matching if available
              if (altIndex1 !== -1) {
                square.Pieces.splice(altIndex1, 1);
                this.addLog(
                  `✅ FALLBACK: Removed piece by type+color at index ${altIndex1}`
                );
              } else if (altIndex2 !== -1) {
                square.Pieces.splice(altIndex2, 1);
                this.addLog(
                  `✅ FALLBACK: Removed piece by type at index ${altIndex2}`
                );
              } else {
                this.addLog(
                  `❌ CRITICAL: No matching piece found with any strategy!`
                );
              }
            }
          }
        }
      }
    } else {
      this.addLog(`🗡️ No side effects to process`);
    }

    // CRITICAL FIX: Handle captures at the destination square
    const fromSquare = testState.board[fromPosition.row][fromPosition.col];
    const toSquare = testState.board[toPosition.row][toPosition.col];

    this.addLog(
      `🗡️ Moving piece from [${fromPosition.row},${fromPosition.col}] to [${toPosition.row},${toPosition.col}]`
    );

    // Check if there are pieces at the destination that need to be captured
    if (toSquare.Pieces.length > 0) {
      this.addLog(
        `🗡️ DESTINATION HAS ${toSquare.Pieces.length} pieces that will be captured:`
      );
      for (const destinationPiece of toSquare.Pieces) {
        this.addLog(
          `🗡️ CAPTURING: ${destinationPiece.isLight ? "Light" : "Dark"} ${
            destinationPiece.type
          } at destination [${toPosition.row},${toPosition.col}]`
        );
      }
      // Remove all pieces at the destination (they get captured)
      toSquare.Pieces = [];
      this.addLog(`✅ Cleared destination square`);
    } else {
      this.addLog(`🗡️ Destination square is empty`);
    }

    // Remove piece from origin
    const pieceIndex = fromSquare.Pieces.findIndex((p) => p.id === piece.id);
    if (pieceIndex !== -1) {
      fromSquare.Pieces.splice(pieceIndex, 1);
      this.addLog(`✅ Removed piece from origin`);
    } else {
      this.addLog(`❌ Could not find piece at origin!`);
    }

    // Add piece to destination
    toSquare.Pieces.push(piece);
    this.addLog(`✅ Added piece to destination`);
  }

  /**
   * Count pieces belonging to the specified color
   */
  private countOurPieces(gameState: GameState, isLight: boolean): number {
    let count = 0;
    const pieces: string[] = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const squarePieces = gameState.board[row][col].Pieces;
        for (const piece of squarePieces) {
          if (piece.isLight === isLight) {
            count++;
            pieces.push(`${piece.type}@[${row},${col}]`);
          }
        }
      }
    }

    // Reduced logging - only log if there's a significant change in piece count
    // (This was causing spam and the bug is now fixed)
    // this.addLog(
    //   `📊 Counted ${count} ${isLight ? "Light" : "Dark"} pieces: ${pieces.join(
    //     ", "
    //   )}`
    // );

    return count;
  }
}

